import React, { useState, useEffect, useRef } from 'react';
import { FaEye, FaEyeSlash, FaLock, FaMobileAlt, FaArrowLeft } from "react-icons/fa";
import { BsPersonCircle, BsShieldCheck } from "react-icons/bs";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SummaryApi from '../common';

// Assets
import oakyaLogo from '../assest/oakya_logo.png';
import sanjivaniLogo from '../assest/sanjivani_university_logo.jpg';
import sanjivaniBuilding from '../assest/sanjivaniBuilding4.webp';

/* ------------------------------------------------------------------
   BRAND COLOURS
------------------------------------------------------------------ */
const BRAND = {
    navy: '#1E3A6E',
    navyDark: '#14284F',
    orange: '#F28C1B',
    yellow: '#FDB913',
    blue: '#2F7FD1',
    green: '#5FAE3E',
};

const EmployeeLogin = () => {
    const [stage, setStage] = useState('welcome');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tempUserData, setTempUserData] = useState(null);
    const [isExiting, setIsExiting] = useState(false);
    const [mobileError, setMobileError] = useState('');

    const [otp, setOtp] = useState(["", "", "", ""]);
    const [sentOtp, setSentOtp] = useState("");
    const [otpExpired, setOtpExpired] = useState(false);
    const otpTimerRef = useRef(null);

    const [data, setData] = useState({
        mobile_number: "",
        password: ""
    });

    const navigate = useNavigate();

    // ------------------------------------------
    // Mobile Number Validation
    // ------------------------------------------
    const validateMobileNumber = (mobile) => {
        const mobileRegex = /^[6-9]\d{9}$/;
        return mobileRegex.test(mobile);
    };

    // ------------------------------------------
    // Attendance Logger & Login Finalization
    // ------------------------------------------
    const handleSuccessfulLogin = async (employeeId, memberType) => {
        sessionStorage.setItem('H7Z4L9VD2XK5JW8QRYT6', btoa(employeeId));
        sessionStorage.setItem('K3M8X1JQ5VZ4B7PWTL9', btoa(memberType));

        // Manage attendance only for "Employee" accounts
        if (memberType?.toLowerCase() === "employee") {
            try {
                // 1. Check if employee already has an attendance record today
                const checkTodayRes = await fetch(SummaryApi.TodayActiveEmployee.url, {
                    method: SummaryApi.TodayActiveEmployee.method || "GET",
                    headers: {
                        'Authorization': 'Bearer sandeep_university_916',
                        'Accept': 'application/json',
                    },
                });

                let existingRecord = null;

                if (checkTodayRes.ok) {
                    const todayData = await checkTodayRes.json();
                    const records = Array.isArray(todayData)
                        ? todayData
                        : Array.isArray(todayData?.data)
                        ? todayData.data
                        : [];

                    existingRecord = records.find(
                        (rec) => String(rec.employee_id) === String(employeeId)
                    );
                }

                if (existingRecord && existingRecord.emp_attendance_id) {
                    // 2. Already logged in today: call update API (POST) to clear logout
                    const updateEndpoint =
                        SummaryApi.EmployeeAttendanceUpdate?.url ||
                        SummaryApi.EmployeeAttendance?.url?.replace("emp_attendance_create.php", "emp_attendance_update.php");

                    await fetch(updateEndpoint, {
                        method: SummaryApi.EmployeeAttendanceUpdate?.method || "POST",
                        headers: {
                            'Authorization': 'Bearer sandeep_university_916',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            emp_attendance_id: existingRecord.emp_attendance_id,
                        }),
                    });
                } else {
                    // 3. First login of the day: create new attendance record (POST)
                    await fetch(SummaryApi.EmployeeAttendance.url, {
                        method: SummaryApi.EmployeeAttendance.method || "POST",
                        headers: {
                            'Authorization': 'Bearer sandeep_university_916',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            employee_id: employeeId,
                        }),
                    });
                }
            } catch (attendanceErr) {
                console.error("Attendance synchronization failed:", attendanceErr);
            }
        }

        setIsExiting(true);
        setTimeout(() => {
            navigate('/');
        }, 550);
    };

    // ------------------------------------------
    // Input Change
    // ------------------------------------------
    const handleOnChange = (e) => {
        const { name, value } = e.target;

        if (name === 'mobile_number') {
            const numericValue = value.replace(/\D/g, '').slice(0, 10);
            setData((prev) => ({ ...prev, [name]: numericValue }));

            if (mobileError) setMobileError('');

            if (numericValue.length === 10) {
                if (!validateMobileNumber(numericValue)) {
                    setMobileError('Mobile number must start with 6, 7, 8, or 9');
                }
            } else if (numericValue.length > 0) {
                setMobileError('');
            }
        } else {
            setData((prev) => ({ ...prev, [name]: value }));
        }
    };

    // ------------------------------------------
    // OTP Change
    // ------------------------------------------
    const handleOtpChange = (e, index) => {
        const value = e.target.value;

        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value !== "") {
            if (index < otp.length - 1) {
                document.getElementById(`otp-input-${index + 1}`)?.focus();
            }
        } else if (e.nativeEvent.inputType === "deleteContentBackward") {
            if (index > 0) {
                document.getElementById(`otp-input-${index - 1}`)?.focus();
            }
        }
    };

    // ------------------------------------------
    // Send OTP
    // ------------------------------------------
    const sendOtp = (mobile_number) => {
        const generatedOtp = Math.floor(1000 + Math.random() * 9000).toString();

        setSentOtp(generatedOtp);
        setOtpExpired(false);
        setOtp(["", "", "", ""]);

        console.log(`Generated OTP for mobile ${mobile_number}: ${generatedOtp}`);
        toast.info("OTP has been generated and displayed in the console.");

        if (otpTimerRef.current) clearTimeout(otpTimerRef.current);

        otpTimerRef.current = setTimeout(() => {
            setOtpExpired(true);
            toast.error("OTP has expired. Please request a new OTP.");
        }, 10 * 60 * 1000);
    };

    useEffect(() => {
        return () => {
            if (otpTimerRef.current) clearTimeout(otpTimerRef.current);
        };
    }, []);

    // ------------------------------------------
    // Login Submit
    // ------------------------------------------
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateMobileNumber(data.mobile_number)) {
            if (data.mobile_number.length !== 10) {
                setMobileError('Mobile number must be exactly 10 digits');
                toast.error('Please enter a valid 10-digit mobile number');
            } else {
                setMobileError('Mobile number must start with 6, 7, 8, or 9');
                toast.error('Mobile number must start with 6, 7, 8, or 9');
            }
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(SummaryApi.verifyEmployee.url, {
                method: SummaryApi.verifyEmployee.method,
                headers: {
                    'Authorization': 'Bearer sandeep_university_916',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    mobile_number: data.mobile_number,
                    password: data.password
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to verify mobile number');
            }

            const responseData = await response.json();
            sessionStorage.setItem('user', JSON.stringify(responseData));

            if (responseData.status) {
                if (responseData.member_type === "Admin") {
                    setTempUserData({
                        employee_id: responseData.employee_id,
                        member_type: responseData.member_type
                    });
                    sendOtp(data.mobile_number);
                    setStage('otp');
                } else {
                    await handleSuccessfulLogin(responseData.employee_id, responseData.member_type);
                }
            } else {
                toast.error(responseData.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to check mobile number existence');
        } finally {
            setLoading(false);
        }
    };

    // ------------------------------------------
    // OTP Submit
    // ------------------------------------------
    const handleOtpSubmit = async (e) => {
        e.preventDefault();

        if (otpExpired) {
            toast.error("OTP has expired. Please request a new OTP.");
            return;
        }

        const enteredOtp = otp.join("");

        if (enteredOtp === sentOtp) {
            toast.success("OTP verified successfully!");
            if (tempUserData) {
                await handleSuccessfulLogin(tempUserData.employee_id, tempUserData.member_type);
            }
        } else {
            toast.error("Invalid OTP entered.");
        }
    };

    return (
        <section
            id="login"
            className={`login-root min-h-screen w-full grid lg:grid-cols-2 overflow-x-hidden ${isExiting ? 'lg-exit' : ''}`}
        >
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

                .login-root { 
                    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
                    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease-in-out;
                }

                .lg-exit {
                    transform: translateX(-100%);
                    opacity: 0;
                    pointer-events: none;
                }

                @keyframes lg-rise {
                    from { opacity: 0; transform: translateY(18px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes lg-pop {
                    from { opacity: 0; transform: scale(.92) translateY(14px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes lg-slide-in {
                    from { opacity: 0; transform: translateX(28px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes lg-spin { to { transform: rotate(360deg); } }
                @keyframes lg-spin-rev { to { transform: rotate(-360deg); } }
                @keyframes lg-float {
                    0%,100% { transform: translateY(0); }
                    50%     { transform: translateY(-8px); }
                }
                @keyframes lg-pulse-ring {
                    0%   { box-shadow: 0 0 0 0 rgba(242,140,27,.55); }
                    100% { box-shadow: 0 0 0 18px rgba(242,140,27,0); }
                }

                .lg-rise     { animation: lg-rise .7s cubic-bezier(.2,.8,.2,1) both; }
                .lg-pop      { animation: lg-pop .55s cubic-bezier(.2,.9,.3,1.1) both; }
                .lg-slide-in { animation: lg-slide-in .45s cubic-bezier(.2,.8,.2,1) both; }
                .lg-float    { animation: lg-float 4.5s ease-in-out infinite; }
                .lg-pulse    { animation: lg-pulse-ring 1.8s ease-out infinite; }

                .lg-stagger > * { animation: lg-rise .5s cubic-bezier(.2,.8,.2,1) both; }
                .lg-stagger > *:nth-child(1) { animation-delay: .08s; }
                .lg-stagger > *:nth-child(2) { animation-delay: .16s; }
                .lg-stagger > *:nth-child(3) { animation-delay: .24s; }
                .lg-stagger > *:nth-child(4) { animation-delay: .32s; }

                .lg-field { position: relative; }
                .lg-field input {
                    width: 100%; height: 56px; padding: 20px 46px 0 46px;
                    border: 1.5px solid #D5DCE8; border-radius: 12px; background: #fff;
                    color: #14284F; font-size: 15px; font-weight: 500; outline: none;
                    transition: border-color .2s, box-shadow .2s;
                }
                .lg-field input:focus {
                    border-color: ${BRAND.navy};
                    box-shadow: 0 0 0 4px rgba(30,58,110,.12);
                }
                .lg-field input.lg-input-error {
                    border-color: #EF4444;
                }
                .lg-field input.lg-input-error:focus {
                    box-shadow: 0 0 0 4px rgba(239,68,68,.15);
                }
                .lg-field label {
                    position: absolute; left: 46px; top: 17px; font-size: 15px;
                    color: #7A8699; pointer-events: none;
                    transition: all .18s ease; transform-origin: left top;
                }
                .lg-field input:focus + label,
                .lg-field input:not(:placeholder-shown) + label {
                    top: 8px; font-size: 11.5px; font-weight: 600; color: ${BRAND.navy};
                }
                .lg-field input.lg-input-error + label {
                    color: #EF4444;
                }
                .lg-field .lg-icon {
                    position: absolute; left: 16px; top: 50%; transform: translateY(-50%);
                    color: #8A96AA; transition: color .2s; font-size: 16px;
                }
                .lg-field input:focus ~ .lg-icon { color: ${BRAND.orange}; }
                .lg-field input.lg-input-error ~ .lg-icon { color: #EF4444; }

                .lg-otp {
                    width: 100%; height: 60px; text-align: center; font-size: 24px; font-weight: 700;
                    border: 1.5px solid #D5DCE8; border-radius: 12px; background: #fff;
                    color: ${BRAND.navyDark}; outline: none; transition: all .2s;
                }
                .lg-otp:focus {
                    border-color: ${BRAND.orange};
                    box-shadow: 0 0 0 4px rgba(242,140,27,.18);
                    transform: translateY(-2px);
                }

                .lg-btn {
                    position: relative; overflow: hidden; width: 100%; height: 54px;
                    border-radius: 12px; border: none; cursor: pointer;
                    background: ${BRAND.navy}; color: #fff; font-weight: 700; font-size: 16px;
                    transition: transform .15s, background .25s, box-shadow .25s;
                    box-shadow: 0 10px 22px -10px rgba(30,58,110,.7);
                }
                .lg-btn::after {
                    content: ''; position: absolute; inset: 0;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,.28), transparent);
                    transform: translateX(-120%); transition: transform .6s;
                }
                .lg-btn:hover:not(:disabled)::after { transform: translateX(120%); }
                .lg-btn:hover:not(:disabled) { background: ${BRAND.navyDark}; }
                .lg-btn:active:not(:disabled) { transform: scale(.98); }
                .lg-btn:disabled { opacity: .75; cursor: not-allowed; }
                .lg-btn:focus-visible, .lg-cta:focus-visible { outline: 3px solid ${BRAND.yellow}; outline-offset: 3px; }

                .lg-cta {
                    border: none; cursor: pointer; padding: 15px 38px; border-radius: 999px;
                    background: ${BRAND.orange}; color: ${BRAND.navyDark}; font-weight: 800; font-size: 16px;
                    transition: transform .2s, background .2s;
                }
                .lg-cta:hover { background: ${BRAND.yellow}; transform: translateY(-2px); }

                .lg-spinner {
                    width: 20px; height: 20px; border-radius: 50%;
                    border: 3px solid rgba(255,255,255,.35); border-top-color: #fff;
                    animation: lg-spin .7s linear infinite; display: inline-block; vertical-align: middle;
                }

                @media (prefers-reduced-motion: reduce) {
                    .login-root *, .login-root *::before, .login-root *::after {
                        animation-duration: .01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .01ms !important;
                    }
                }
            `}</style>

            {/* LEFT SECTION */}
            <div
                className="relative overflow-hidden flex flex-col justify-between px-6 py-8 sm:px-10 lg:px-[7%] lg:py-10"
                style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFBFD 55%, #F0F1F6 100%)' }}
            >
                <div
                    className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full opacity-[.10] blur-3xl"
                    style={{ background: BRAND.orange }}
                />

                {/* LOGOS */}
                <div className="relative lg-rise flex items-center gap-4 sm:gap-6 flex-wrap">
                    <img
                        src={oakyaLogo}
                        alt="Oakya IT Services"
                        className="h-11 sm:h-14 w-auto object-contain"
                    />
                    <span className="h-10 sm:h-12 w-px bg-slate-300" />
                    <span className="text-[13px] sm:text-[15px] text-slate-500 whitespace-nowrap">
                        in partnership with
                    </span>
                    <img
                        src={sanjivaniLogo}
                        alt="Sanjivani University"
                        className="h-16 sm:h-24 w-auto object-contain"
                    />
                </div>

                {/* HERO HEADLINE + IMAGE */}
                <div className="relative flex-1 flex flex-col lg:flex-row items-center gap-8 lg:gap-8 my-6 lg:my-0 mr-4">
                    <div className="w-[90%] sm:w-[77%] lg:w-[59%] max-w-[490px] shrink-0 flex items-center justify-center lg-rise my-3 sm:my-5 lg:my-0 lg:mr-2">
                        <img
                            src={sanjivaniBuilding}
                            alt="Sanjivani University Campus"
                            className="w-full h-auto object-contain drop-shadow-xl select-none -mb-12"
                            style={{ transformOrigin: 'center center' }}
                        />
                    </div>

                    <div className="lg:flex-1 text-center lg:text-left z-10 lg:pl-2">
                        <h1
                            className="lg-rise font-extrabold uppercase leading-[1.04] tracking-tight"
                            style={{
                                color: '#2B3556',
                                fontSize: 'clamp(30px, 3.3vw, 54px)',
                                animationDelay: '.2s',
                            }}
                        >
                            Empower.<br />Explore.<br />Execute.
                        </h1>

                        <p
                            className="lg-rise mt-4 text-[15px] sm:text-base lg:text-[17px] leading-snug max-w-[340px] mx-auto lg:mx-0 text-slate-700"
                            style={{ animationDelay: '.3s' }}
                        >
                            A secure, unified portal for Oakya IT Services and Sanjivani University.
                        </p>
                    </div>
                </div>

                {/* FOOTER */}
                <div className="relative hidden lg:block text-[13px] text-slate-500 leading-7">
                    <p>© 2026 Oakya IT Services</p>
                </div>
            </div>

            {/* RIGHT SECTION */}
            <div
                className="relative overflow-hidden flex items-center justify-center px-4 sm:px-8 py-10 min-h-[560px]"
                style={{ background: `linear-gradient(155deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)` }}
            >
                {/* Orbit rings */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div
                        className="absolute w-[520px] h-[520px] rounded-full border border-white/10"
                        style={{ animation: 'lg-spin 40s linear infinite' }}
                    >
                        <span className="absolute -top-2 left-1/2 w-4 h-4 rounded-full" style={{ background: BRAND.orange }} />
                        <span className="absolute top-1/2 -right-2 w-3 h-3 rounded-full" style={{ background: BRAND.green }} />
                    </div>
                    <div
                        className="absolute w-[760px] h-[760px] rounded-full border border-white/[.07]"
                        style={{ animation: 'lg-spin-rev 60s linear infinite' }}
                    >
                        <span className="absolute -bottom-2 left-1/3 w-4 h-4 rounded-full" style={{ background: BRAND.blue }} />
                        <span className="absolute top-1/4 -left-2 w-3 h-3 rounded-full" style={{ background: BRAND.yellow }} />
                    </div>
                </div>

                {/* STAGE: WELCOME */}
                {stage === 'welcome' && (
                    <div className="relative z-10 text-center max-w-sm lg-pop">
                        <div className="lg-float mx-auto mb-6 w-20 h-20 rounded-full flex items-center justify-center bg-white lg-pulse">
                            <BsPersonCircle size={50} color={BRAND.navy} />
                        </div>

                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                            Welcome back
                        </h2>

                        <button
                            type="button"
                            className="lg-cta mt-10"
                            onClick={() => setStage('form')}
                        >
                            Login
                        </button>
                    </div>
                )}

                {/* STAGE: LOGIN FORM */}
                {stage === 'form' && (
                    <div
                        key="form"
                        className="relative z-10 w-full max-w-[420px] bg-white rounded-3xl px-6 py-8 sm:px-9 sm:py-10 lg-pop"
                        style={{ boxShadow: '0 30px 70px -20px rgba(0,0,0,.55)' }}
                    >
                        <div className="absolute top-0 left-8 right-8 h-1 rounded-b-full flex overflow-hidden">
                            <span className="flex-1" style={{ background: BRAND.orange }} />
                            <span className="flex-1" style={{ background: BRAND.yellow }} />
                            <span className="flex-1" style={{ background: BRAND.green }} />
                            <span className="flex-1" style={{ background: BRAND.blue }} />
                        </div>

                        <button
                            type="button"
                            onClick={() => setStage('welcome')}
                            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                        >
                            <FaArrowLeft size={12} /> Back
                        </button>

                        <h2 className="text-2xl sm:text-[26px] font-extrabold" style={{ color: BRAND.navyDark }}>
                            Login
                        </h2>
                        <p className="mt-1.5 mb-6 text-sm leading-6 text-slate-500">
                            Enter your mobile number and password to continue.
                        </p>

                        <form onSubmit={handleSubmit} className="lg-stagger flex flex-col gap-4">
                            <div className="lg-field">
                                <input
                                    id="mobile_number"
                                    type="tel"
                                    name="mobile_number"
                                    value={data.mobile_number}
                                    onChange={handleOnChange}
                                    placeholder=" "
                                    autoComplete="tel"
                                    maxLength={10}
                                    className={mobileError ? 'lg-input-error' : ''}
                                    required
                                />
                                <label htmlFor="mobile_number">Mobile number</label>
                                <FaMobileAlt className="lg-icon" />
                            </div>
                            {mobileError && (
                                <p className="text-red-500 text-xs -mt-2 ml-1">{mobileError}</p>
                            )}

                            <div className="lg-field">
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={data.password}
                                    onChange={handleOnChange}
                                    placeholder=" "
                                    autoComplete="current-password"
                                    required
                                />
                                <label htmlFor="password">Password</label>
                                <FaLock className="lg-icon" />

                                <button
                                    type="button"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-800 transition-colors"
                                >
                                    {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                </button>
                            </div>

                            <button type="submit" className="lg-btn mt-1" disabled={loading}>
                                {loading ? <span className="lg-spinner" /> : "Login"}
                            </button>
                        </form>
                    </div>
                )}

                {/* STAGE: OTP */}
                {stage === 'otp' && (
                    <div
                        key="otp"
                        className="relative z-10 w-full max-w-[420px] bg-white rounded-3xl px-6 py-8 sm:px-9 sm:py-10 lg-slide-in"
                        style={{ boxShadow: '0 30px 70px -20px rgba(0,0,0,.55)' }}
                    >
                        <div className="absolute top-0 left-8 right-8 h-1 rounded-b-full flex overflow-hidden">
                            <span className="flex-1" style={{ background: BRAND.orange }} />
                            <span className="flex-1" style={{ background: BRAND.yellow }} />
                            <span className="flex-1" style={{ background: BRAND.green }} />
                            <span className="flex-1" style={{ background: BRAND.blue }} />
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                setOtp(["", "", "", ""]);
                                setStage('form');
                            }}
                            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                        >
                            <FaArrowLeft size={12} /> Back
                        </button>

                        <div
                            className="w-12 h-12 rounded-2xl flex items-center justify-center text-white mb-4"
                            style={{ background: BRAND.orange }}
                        >
                            <BsShieldCheck size={24} />
                        </div>

                        <h2 className="text-2xl sm:text-[26px] font-extrabold" style={{ color: BRAND.navyDark }}>
                            Verify it's you
                        </h2>
                        <p className="mt-1.5 mb-6 text-sm leading-6 text-slate-500">
                            Enter the 4-digit code sent for {data.mobile_number}.
                        </p>

                        <form onSubmit={handleOtpSubmit} className="flex flex-col gap-6">
                            <div className="grid grid-cols-4 gap-3">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        id={`otp-input-${index}`}
                                        className="lg-otp"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength="1"
                                        value={digit}
                                        onChange={(e) => handleOtpChange(e, index)}
                                        required
                                        autoFocus={index === 0}
                                        aria-label={`OTP digit ${index + 1}`}
                                    />
                                ))}
                            </div>

                            <button type="submit" className="lg-btn">
                                Verify OTP
                            </button>

                            <button
                                type="button"
                                onClick={() => sendOtp(data.mobile_number)}
                                className="text-sm font-semibold hover:underline"
                                style={{ color: BRAND.navy }}
                            >
                                Send a new code
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </section>
    );
};

export default EmployeeLogin;